import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { buildChecks, summarize, fmtAgo, fmtClock } from '../lib/commanderHealth';

const API_BASE = 'https://teardrophydro.co.uk/garageFarm';
const POLL_MS = 30_000;
const LS_OPEN = 'fd2.commanderLogsOpen';

const readOpen = () => { try { return localStorage.getItem(LS_OPEN) === '1'; } catch { return false; } };

// Front-page health panel for FarmCommander (the pm2 process that runs the
// flood schedule, AC and dosing). Checks come from lib/commanderHealth.js.
export default function CommanderStatusPanel() {
  const [payload, setPayload] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [open, setOpen] = useState(readOpen);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/farm-commander/status`);
      setPayload(res.data);
      setLoadError('');
    } catch (err) {
      setLoadError(err.response?.data?.error || err.message || 'Request failed');
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const t = setInterval(fetchStatus, POLL_MS);
    return () => clearInterval(t);
  }, [fetchStatus]);

  const toggle = () => {
    setOpen(o => {
      try { localStorage.setItem(LS_OPEN, o ? '0' : '1'); } catch { /* private mode */ }
      return !o;
    });
  };

  const checks = useMemo(() => (payload ? buildChecks(payload) : []), [payload]);
  const { tone, headline } = loadError
    ? { tone: 'unknown', headline: "Can't load status" }
    : payload ? summarize(checks) : { tone: 'unknown', headline: 'Loading…' };

  const now = payload ? new Date(payload.serverTime).getTime() : Date.now();
  const actions = payload?.commander?.actions || [];
  const errors = payload?.commander?.errors || [];
  const last = actions[0];

  return (
    <section className={`fd2-cmd fd2-cmd--${tone}`} aria-label="Farm Commander status">
      <div className="fd2-cmd-head">
        <span className="fd2-cmd-dot" aria-hidden="true" />
        <h2 className="fd2-cmd-title">Farm Commander</h2>
        <span className="fd2-cmd-headline">{headline}</span>
        {last && (
          <span className="fd2-cmd-last" title={last.summary}>
            Last: {last.summary} · {fmtAgo(now - new Date(last.at).getTime())}
          </span>
        )}
        <button type="button" className="fd2-cmd-toggle" onClick={toggle} aria-expanded={open}>
          {open ? 'Hide' : 'Show'} activity & errors
          {errors.length > 0 && <span className="fd2-cmd-badge">{errors.length}</span>}
        </button>
      </div>

      {loadError && <div className="fd2-cmd-loaderr">{loadError}</div>}

      {checks.length > 0 && (
        <ul className="fd2-cmd-checks">
          {checks.map(c => (
            <li key={c.key} className={`fd2-cmd-check fd2-cmd-check--${c.tone}`} title={c.detail || ''}>
              <span className="fd2-cmd-check-label">{c.label}</span>
              <span className="fd2-cmd-check-value">{c.value}</span>
              {c.detail && <span className="fd2-cmd-check-detail">{c.detail}</span>}
            </li>
          ))}
        </ul>
      )}

      {open && payload && (
        <div className="fd2-cmd-logs">
          <div className="fd2-cmd-log">
            <h3 className="fd2-cmd-log-title">Recent activity</h3>
            {actions.length === 0 ? <p className="fd2-cmd-empty">Nothing recorded since last restart.</p> : (
              <ol className="fd2-cmd-list">
                {actions.slice(0, 15).map((a, i) => (
                  <li key={i} className={a.ok ? '' : 'is-failed'}>
                    <time>{fmtClock(a.at)}</time>
                    <span className="fd2-cmd-kind">{a.kind}</span>
                    <span className="fd2-cmd-msg">{a.summary}</span>
                  </li>
                ))}
              </ol>
            )}
          </div>
          <div className="fd2-cmd-log">
            <h3 className="fd2-cmd-log-title">Errors & warnings</h3>
            {errors.length === 0 ? <p className="fd2-cmd-empty">None since last restart.</p> : (
              <ol className="fd2-cmd-list">
                {errors.slice(0, 15).map((e, i) => (
                  <li key={i} className={e.level === 'error' ? 'is-failed' : 'is-warn'}>
                    <time>{e.count > 1 ? `${fmtClock(e.at)}–${fmtClock(e.lastAt)}` : fmtClock(e.at)}</time>
                    <span className="fd2-cmd-kind">{e.count > 1 ? `×${e.count}` : e.level}</span>
                    <span className="fd2-cmd-msg fd2-cmd-msg--mono">{e.message}</span>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
